import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerJournalCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type {
  JournalEntryDto,
  ReverseJournalEntryRequestDto,
} from "@/features/ledger/api/dto";

/**
 * Contre-passation manuelle d'une écriture (`POST /JournalEntries/{id}/reverse`,
 * guide §6.4) — action SENSIBLE réservée à flashpay-superadmin / flashpay-admin.
 *
 * Idempotency-Key OBLIGATOIRE + idempotence serveur sur `approvalRequestId`
 * (workflow LEDGER_MANUAL_REVERSAL au statut APPROVED, vérifié en direct
 * auprès d'admin-service).
 *
 * Action protégée maker-checker (guide §5.4) : ce hook est l'EXÉCUTEUR
 * appelé par la page détail d'une ApprovalRequest (`routes/admin/`) après
 * approbation ; la création de la demande passe par
 * `useSubmitApprovalRequest` (`@/features/shared`) depuis le dialog de
 * contre-passation.
 */
export const useReverseJournalEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "journal-entries", "reverse"],
    mutationFn: ({
      journalEntryId,
      body,
    }: {
      journalEntryId: string;
      body: ReverseJournalEntryRequestDto;
    }) =>
      mutateIdempotent<JournalEntryDto>({
        scopeId: `ledger.journal-entries.reverse.${journalEntryId}`,
        url: ledgerEndpoints.journalEntries.reverse(journalEntryId),
        body,
      }),
    onSuccess: (_data, { journalEntryId }) => {
      return invalidateAfterMutation(
        queryClient,
        ledgerJournalCacheKeys(journalEntryId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useReverseJournalEntry failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
